import "./AuthForms.css";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "./supabaseClient";

export default function EmployerAuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() => searchParams.get('mode') === 'signup' ? 'signup' : 'signin'); // "signin" | "signup"
  const [companyName, setCompanyName] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactTitle, setContactTitle] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const formRef = useRef(null);

  // AutoFill can update the DOM without first delivering a React change event.
  const readCredentials = () => {
    const fields = formRef.current.elements;
    return {
      email: fields.namedItem('email').value,
      password: fields.namedItem('password')?.value || '',
      confirmPassword: fields.namedItem('confirmPassword')?.value || '',
    };
  };
  // Replace the native input on explicit Show/Hide to clear stale AutoFill masking.
  const togglePassword = (confirmation = false) => {
    const current = readCredentials();
    setEmail(current.email);
    setPassword(current.password);
    setConfirmPassword(current.confirmPassword);
    if (confirmation) setShowConfirmPassword(value => !value);
    else setShowPassword(value => !value);
  };

  const handleSubmit = async () => {
    const { email, password, confirmPassword } = readCredentials();
    setEmail(email);
    setPassword(password);
    setConfirmPassword(confirmPassword);
    setLoading(true);
    setError(null);
    setMessage(null);

    if (mode === "signup") {
      if (!companyName.trim()) {
        setError("Please enter your company name.");
        setLoading(false);
        return;
      }
      if (!contactName.trim()) {
        setError("Please enter your name.");
        setLoading(false);
        return;
      }
      if (!inviteCode.trim()) {
        setError("An employer early-access code is required during the beta.");
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        setLoading(false);
        return;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        setLoading(false);
        return;
      }

      // Pre-validate invite code BEFORE creating the auth user, same discipline
      // as the veteran signup flow: avoids orphan auth.users rows on a bad code.
      const { data: checkData, error: checkError } = await supabase.rpc(
        'check_employer_invite_code',
        { p_code: inviteCode.trim() }
      );

      if (checkError || !checkData?.valid) {
        const checkReason = checkData?.reason;
        let checkMsg = "Unable to validate your access code. Please try again.";
        if (checkReason === 'missing_code') checkMsg = "Enter your employer early-access code.";
        if (checkReason === 'invalid_code') checkMsg = "That access code was not found.";
        if (checkReason === 'already_used') checkMsg = "That access code has already been used.";
        setError(checkMsg);
        setLoading(false);
        return;
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            account_type: 'employer',
            company_name: companyName.trim(),
            company_website: companyWebsite.trim(), industry: industry.trim(), company_size: companySize,
            contact_name: contactName.trim(), contact_title: contactTitle.trim(),
          },
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      // Email-confirmed signup has no session yet. Approval remains server gated.
      // The signed-in activation form retries with the account's own invite code.
      if (!signUpData?.session?.user) {
        setMessage("Check your email to confirm your account. Keep your invite code, then sign in to finish beta access.");
        setLoading(false);
        return;
      }

      // Redeem server-side. SECURITY DEFINER RPC locks the row, rejects
      // unknown/used codes, and creates employer_profiles atomically.
      const newUserId = signUpData.session.user.id;
      if (newUserId) {
        const { data: redeemData, error: redeemError } = await supabase.rpc(
          'redeem_employer_invite_code',
          {
            p_code: inviteCode.trim(),
            p_user_id: newUserId,
            p_company_name: companyName.trim(),
            p_company_website: companyWebsite.trim() || null,
            p_industry: industry.trim() || null,
            p_company_size: companySize.trim() || null,
            p_contact_name: contactName.trim() || null,
            p_contact_title: contactTitle.trim() || null,
          }
        );

        if (redeemError || !redeemData?.ok) {
          const reason = redeemData?.reason;
          let msg = "Account was created, but access code redemption failed. Contact support.";
          if (reason === 'missing_code') msg = "Access code was missing after signup. Contact support.";
          if (reason === 'missing_user') msg = "Account was created, but user ID was missing. Contact support.";
          if (reason === 'invalid_code') msg = "Account was created, but the access code could not be found. Contact support.";
          if (reason === 'already_used') msg = "Account was created, but that access code was already used. Contact support.";
          setError(msg);
          setLoading(false);
          return;
        }
      }

      setMessage("Your beta access is ready.");
      // If email confirmation is disabled for this project, signUp already
      // produced an active session before employer_profiles existed (see
      // App.js) — re-resolve now that redemption has actually completed.
      window.dispatchEvent(new Event('vq:refresh-account-type'));
      setLoading(false);
      return;
    }

    // Sign in
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) setError(signInError.message);

    setLoading(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setMessage(null);
    setPassword("");
    setConfirmPassword("");
    setCompanyName("");
    setCompanyWebsite("");
    setIndustry("");
    setCompanySize("");
    setContactName("");
    setContactTitle("");
    setInviteCode("");
  };


  return (
    <div className="vq-auth" style={S.root}>
      <div style={S.bg} />
      <form ref={formRef} className="vq-auth-card" style={S.card} noValidate onSubmit={event => { event.preventDefault(); handleSubmit(); }}>

        <Link to="/employers" style={S.logo}>Vet<span style={{ color: "#fff" }}>Qualified</span> <span style={S.logoTag}>for Employers</span></Link>

        <h1 style={S.heading}>
          {mode === "signin" ? "Employer sign in" : "Create your employer account"}
        </h1>
        <p style={S.sub}>
          {mode === "signin" && "Sign in to your VetQualified employer account."}
          {mode === "signup" && "Employer access is early-access only during the beta — you'll need a code from us."}
        </p>

        {mode === "signup" && (
          <>
            <div style={S.field}>
              <label style={S.label} htmlFor="employer-companyName">Company Name</label>
              <input id="employer-companyName"
                style={S.input}
                type="text"
                placeholder="Acme Corp"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}

              />
            </div>

            <div className="vq-auth-row" style={S.nameRow}>
              <div style={{ ...S.field, flex: 1, minWidth: 0 }}>
                <label style={S.label} htmlFor="employer-contactName">Your Name</label>
                <input id="employer-contactName"
                  style={S.input}
                  type="text"
                  placeholder="Jane Smith"
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}

                />
              </div>
              <div style={{ ...S.field, flex: 1, minWidth: 0 }}>
                <label style={S.label} htmlFor="employer-contactTitle">Your Title</label>
                <input id="employer-contactTitle"
                  style={S.input}
                  type="text"
                  placeholder="Talent Acquisition"
                  value={contactTitle}
                  onChange={e => setContactTitle(e.target.value)}

                />
              </div>
            </div>

            <div className="vq-auth-row" style={S.nameRow}>
              <div style={{ ...S.field, flex: 1, minWidth: 0 }}>
                <label style={S.label} htmlFor="employer-companyWebsite">Company Website</label>
                <input id="employer-companyWebsite"
                  style={S.input}
                  type="text"
                  placeholder="acme.com"
                  value={companyWebsite}
                  onChange={e => setCompanyWebsite(e.target.value)}

                />
              </div>
              <div style={{ ...S.field, flex: 1, minWidth: 0 }}>
                <label style={S.label} htmlFor="employer-companySize">Company Size</label>
                <select id="employer-companySize" style={S.select} value={companySize} onChange={e => setCompanySize(e.target.value)}>
                  <option value="">Select…</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-200">51–200</option>
                  <option value="201-1000">201–1,000</option>
                  <option value="1000+">1,000+</option>
                </select>
              </div>
            </div>

            <div style={S.field}>
              <label style={S.label} htmlFor="employer-industry">Industry</label>
              <input id="employer-industry"
                style={S.input}
                type="text"
                placeholder="Defense, Technology, Logistics…"
                value={industry}
                onChange={e => setIndustry(e.target.value)}

              />
            </div>

            <div style={S.field}>
              <label style={S.label} htmlFor="employer-inviteCode">Employer Access Code</label>
              <input id="employer-inviteCode"
                style={S.input}
                type="text"
                placeholder="VQE-XXXXX-XXX"
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value)}

                autoComplete="off"
              />
              <div style={S.hint}>Employer access is early-access only right now — request a code from the <Link to="/employers" style={S.link}>Employers page</Link>.</div>
            </div>
          </>
        )}

        <div style={S.field}>
          <label style={S.label} htmlFor="employer-email">Email</label>
          <input id="employer-email" name="email"
            style={S.input}
            type="email"
            autoComplete="username"
            placeholder="you@company.com"
            value={email}
            onChange={e => setEmail(e.target.value)}

          />
        </div>

        <div style={S.field}>
          <label style={S.label} htmlFor="employer-password">Password</label>
          <div style={S.inputWrap}>
            <input key={showPassword ? "password-visible" : "password-hidden"} id="employer-password" name="password" autoCapitalize="none" spellCheck={false}
              style={{ ...S.input, paddingRight: "4.5rem" }}
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}

              autoComplete={mode === "signin" ? "current-password" : "new-password"}
            />
            <button style={S.eyeBtn} onClick={() => togglePassword()} aria-label={showPassword ? "Hide password" : "Show password"} aria-controls="employer-password" aria-pressed={showPassword} type="button">
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {mode === "signup" && (
          <div style={S.field}>
            <label style={S.label} htmlFor="employer-confirmPassword">Confirm Password</label>
            <div style={S.inputWrap}>
              <input key={showConfirmPassword ? "confirmation-visible" : "confirmation-hidden"} id="employer-confirmPassword" name="confirmPassword" autoCapitalize="none" spellCheck={false}
                style={{ ...S.input, paddingRight: "4.5rem" }}
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Enter the same password again"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}

                aria-describedby="employer-confirm-help"
                autoComplete="new-password"
              />
              <button style={S.eyeBtn} onClick={() => togglePassword(true)} aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"} aria-controls="employer-confirmPassword" aria-pressed={showConfirmPassword} type="button">
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
            <p id="employer-confirm-help" style={S.hint}>Type or paste the same password in both fields.</p>
          </div>
        )}

        {error && <div role="alert" style={S.error}>{error}</div>}
        {message && <div role="status" style={S.success}>{message}</div>}

        <button
          style={{ ...S.btn, ...(loading ? S.btnLoading : {}) }}
          type="submit"
          disabled={loading}
        >
          {loading ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Employer Account"}
        </button>

        {mode === "signin" && (
          <div style={S.toggle}>
            Have an employer access code?{" "}
            <button type="button" className="vq-auth-mode" style={S.link} onClick={() => switchMode("signup")}>Create an account</button>
          </div>
        )}

        {mode === "signup" && (
          <div style={S.toggle}>
            Already have an employer account?{" "}
            <button type="button" className="vq-auth-mode" style={S.link} onClick={() => switchMode("signin")}>Sign in</button>
          </div>
        )}

        <div style={S.memberToggle}>
          <Link to="/auth" style={S.link}>I'm a veteran, not an employer →</Link>
        </div>

        <p style={S.toggle}><Link to="/privacy" style={S.link}>Privacy &amp; safety</Link></p>
      </form>
    </div>
  );
}

const S = {
  root: { fontFamily: "'DM Sans', sans-serif", background: "#0B1F3A", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" },
  bg: { position: "fixed", inset: 0, background: "radial-gradient(ellipse 70% 50% at 15% 20%, rgba(201,168,76,0.07) 0%, transparent 60%), repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(201,168,76,0.03) 59px, rgba(201,168,76,0.03) 60px), repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(201,168,76,0.03) 59px, rgba(201,168,76,0.03) 60px)", pointerEvents: "none", zIndex: 0 },
  card: { position: "relative", zIndex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 16, padding: "2.5rem", width: "100%", maxWidth: 460 },
  logo: { display: "block", fontFamily: "'DM Serif Display', serif", fontSize: "1.5rem", color: "#C9A84C", marginBottom: "1.5rem", letterSpacing: "-0.02em", textDecoration: "none" },
  logoTag: { fontFamily: "'DM Sans', sans-serif", fontSize: "0.7rem", color: "#8FA3BE", fontWeight: 600, letterSpacing: "0.04em", verticalAlign: "middle" },
  heading: { fontFamily: "'DM Serif Display', serif", fontSize: "1.75rem", color: "#fff", marginBottom: "0.5rem", letterSpacing: "-0.02em" },
  sub: { fontSize: "0.85rem", color: "#8FA3BE", marginBottom: "1.75rem", lineHeight: 1.6 },
  nameRow: { display: "flex", gap: "0.75rem", marginBottom: "0rem" },
  field: { marginBottom: "1rem" },
  label: { display: "block", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8FA3BE", marginBottom: "0.4rem" },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  input: { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "0.7rem 0.9rem", boxSizing: "border-box" },
  eyeBtn: { position: "absolute", right: 2, width: 64, height: 44, justifyContent: "center", background: "transparent", border: "none", color: "#8FA3BE", cursor: "pointer", display: "flex", alignItems: "center", padding: 4, font: "inherit", fontSize: "0.78rem", fontWeight: 700 },
  error: { fontSize: "0.78rem", color: "#f87171", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "1rem" },
  success: { fontSize: "0.78rem", color: "#4ade80", background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "1rem" },
  btn: { width: "100%", padding: "0.8rem", background: "#C9A84C", border: "none", borderRadius: 8, color: "#0B1F3A", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", fontWeight: 800, cursor: "pointer", letterSpacing: "0.04em", marginBottom: "1rem" },
  btnLoading: { background: "#A88830", cursor: "not-allowed" },
  toggle: { fontSize: "0.78rem", color: "#8FA3BE", textAlign: "center" },
  link: { color: "#C9A84C", cursor: "pointer", fontWeight: 600, textDecoration: "none" },
  hint: { fontSize: "0.7rem", color: "#8FA3BE", marginTop: "0.4rem", lineHeight: 1.5 },
  select: { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "0.7rem 0.9rem", boxSizing: "border-box", appearance: "none", cursor: "pointer" },
  memberToggle: { textAlign: "center", marginTop: "1.5rem", paddingTop: "1.25rem", borderTop: "1px solid rgba(201,168,76,0.15)" },
};
